import type {
  BasicTableV3ActiveFilter,
  BasicTableV3Column,
  BasicTableV3ColumnFilterConfig,
  BasicTableV3FilterOperator,
  BasicTableV3ResolvedFilterColumn,
} from "@/components/table/v3/BasicTableV3/BasicTableV3.types";
import type { FilterFn, RowData } from "@tanstack/react-table";

/** The tanstack column-filter payload written by `filtersToColumnFilters` and
 *  read back by `facetFilterFn`. */
export interface BasicTableV3EnumColumnFilterValue {
  matchesNothing?: boolean;
  operator: BasicTableV3FilterOperator;
  value: string | string[];
}

// Enum chips: pick one or more of the distinct values a column takes,
// "is any of" (default) or "is not" the matches.
export const BASIC_TABLE_V3_ENUM_FILTER_OPERATORS = [
  "is any of",
  "is not",
] as const satisfies readonly BasicTableV3FilterOperator[];

export const getBasicTableV3EnumFilterSelectedValues = (value: string | string[]): string[] =>
  (Array.isArray(value) ? value : [value]).filter((entry) => entry.trim() !== "");

/** A row's values for an enum-filterable column, from a cell holding either one
 *  value or several. A missing or blank cell contributes no value, so it is
 *  excluded from the chip's options rather than offered as an empty one. */
export const getBasicTableV3EnumRowValues = (
  value: string | string[] | undefined,
): readonly string[] =>
  Array.isArray(value) ? value : value == null || value.trim() === "" ? [] : [value];

export interface BasicTableV3EnumFilterHelpers<
  TData extends RowData,
  TColumnId extends string = string,
> {
  createFilter: (
    column: Pick<BasicTableV3ResolvedFilterColumn<TData, TColumnId>, "id" | "filterConfig">,
    overrides?: Partial<Pick<BasicTableV3ActiveFilter<TColumnId>, "operator" | "value">>,
  ) => BasicTableV3ActiveFilter<TColumnId>;
  facetFilterFn: FilterFn<TData>;
  filtersToColumnFilters: (
    activeFilters: readonly BasicTableV3ActiveFilter<TColumnId>[],
  ) => Array<{ id: TColumnId; value: BasicTableV3EnumColumnFilterValue }>;
  getEnumFilterConfig: (columnId: TColumnId) => BasicTableV3ColumnFilterConfig<TData>;
  getUniqueValues: (columnId: TColumnId) => (row: TData) => string[];
  hasFilterValue: (
    filter: Pick<BasicTableV3ActiveFilter<TColumnId>, "matchesNothing" | "value">,
  ) => boolean;
  resolveFilterColumns: (
    columns: readonly BasicTableV3Column<TData>[],
    data: readonly TData[],
  ) => Array<BasicTableV3ResolvedFilterColumn<TData, TColumnId>>;
}

/**
 * The standard "is any of / is not" enum-chip machinery shared by chip-filtered
 * tables (the same system as the resolvers and agent-conversations pages).
 *
 * `getRowFilterValues` is the one table-specific piece: the stringified values
 * a filterable column takes for a given row (multi-valued columns like tags
 * return several; missing values return `[]` and are excluded from the chip's
 * options). Everything returned here derives from it:
 *
 * - attach `facetFilterFn` + `getEnumFilterConfig(columnId)` to each filterable
 *   column (spread extra config such as `getValueLabel` on top), plus
 *   `getUniqueValues(columnId)` on any column whose accessor is not already the
 *   filter value,
 * - pass `createFilter`, `filtersToColumnFilters`, and `resolveFilterColumns`
 *   to `BasicTableV3Provider`,
 * - render `TableV3FilterArea` (and optionally `BasicTableV3GhostFilterChips` /
 *   `BasicTableV3AddFilterSelect`) with the same `createFilter`.
 */
export const createBasicTableV3EnumFilterHelpers = <
  TData extends RowData,
  TColumnId extends string = string,
>(
  getRowFilterValues: (row: TData, columnId: TColumnId) => string[],
): BasicTableV3EnumFilterHelpers<TData, TColumnId> => {
  const facetFilterFn: FilterFn<TData> = (row, columnId, filterValue) => {
    const { matchesNothing, operator, value } = filterValue as BasicTableV3EnumColumnFilterValue;
    const selected = getBasicTableV3EnumFilterSelectedValues(value);
    if (selected.length === 0) {
      return matchesNothing !== true;
    }
    const values = getRowFilterValues(row.original, columnId as TColumnId);
    const matches = values.some((rowValue) => selected.includes(rowValue));
    // Unknown operators behave as the positive match, so only "is not" negates.
    return operator === "is not" ? !matches : matches;
  };

  const getEnumFilterConfig = (columnId: TColumnId): BasicTableV3ColumnFilterConfig<TData> => ({
    defaultOperator: "is any of",
    defaultValue: [],
    isMultiValue: true,
    operatorOptions: BASIC_TABLE_V3_ENUM_FILTER_OPERATORS,
    type: "enum",
    getValueOptions: (rows) =>
      Array.from(new Set(rows.flatMap((row) => getRowFilterValues(row, columnId)))).sort(),
  });

  // Faceted counts come from tanstack's `getUniqueValues`, which otherwise
  // falls back to the column's accessor value. A column whose accessor is not
  // the filter value (a raw enum behind a rendered label, a proto message, a
  // boolean) then facets every chip option at 0, and
  // `isBasicTableV3ValueOptionDisabled` renders a count of 0 as unselectable —
  // so the chip lists the right options and refuses all of them.
  const getUniqueValues =
    (columnId: TColumnId) =>
    (row: TData): string[] =>
      getRowFilterValues(row, columnId);

  const resolveFilterColumns = (
    columns: readonly BasicTableV3Column<TData>[],
    data: readonly TData[],
  ): Array<BasicTableV3ResolvedFilterColumn<TData, TColumnId>> =>
    columns.flatMap((column) => {
      if (column.filterConfig == null) {
        return [];
      }
      const resolvedValueOptions = Array.from(
        new Set(
          (
            column.filterConfig.getValueOptions?.(data) ??
            column.filterConfig.valueOptions ??
            []
          ).filter(Boolean),
        ),
      ).sort();
      if (resolvedValueOptions.length === 0) {
        return [];
      }
      return [
        {
          ...(column as BasicTableV3ResolvedFilterColumn<TData, TColumnId>),
          resolvedValueOptions,
        },
      ];
    });

  const createFilter = (
    column: Pick<BasicTableV3ResolvedFilterColumn<TData, TColumnId>, "id" | "filterConfig">,
    overrides?: Partial<Pick<BasicTableV3ActiveFilter<TColumnId>, "operator" | "value">>,
  ): BasicTableV3ActiveFilter<TColumnId> => ({
    columnId: column.id,
    id: column.id,
    matchesNothing: false,
    operator: overrides?.operator ?? column.filterConfig.defaultOperator,
    value: overrides?.value ?? column.filterConfig.defaultValue,
  });

  const hasFilterValue = (
    filter: Pick<BasicTableV3ActiveFilter<TColumnId>, "matchesNothing" | "value">,
  ): boolean =>
    getBasicTableV3EnumFilterSelectedValues(filter.value).length > 0 ||
    filter.matchesNothing === true;

  const filtersToColumnFilters = (
    activeFilters: readonly BasicTableV3ActiveFilter<TColumnId>[],
  ): Array<{ id: TColumnId; value: BasicTableV3EnumColumnFilterValue }> =>
    activeFilters.filter(hasFilterValue).map((filter) => ({
      id: filter.columnId,
      value: {
        matchesNothing: filter.matchesNothing,
        operator: filter.operator,
        value: filter.value,
      } satisfies BasicTableV3EnumColumnFilterValue,
    }));

  return {
    createFilter,
    facetFilterFn,
    filtersToColumnFilters,
    getEnumFilterConfig,
    getUniqueValues,
    hasFilterValue,
    resolveFilterColumns,
  };
};
